import base64 from 'react-native-base64';
import { firebase } from '../config/configureFirebase';
import AccessKey from './AccessKey';
import store from '../store/store';
import {
    PLACES_MIN_CHARS,
    PLACES_DEBOUNCE_MS,
    CACHE_TTL_PLACES_MS,
    CACHE_TTL_GEOCODING_MS,
    CACHE_TTL_ROUTES_MS,
} from '../config/economyConfig';
import { debugLog } from './economyLogger';
import {
    shouldCallGoogleRoutes,
    recordRouteApiCall,
    getCachedRoute,
    saveCachedRoute,
    buildHaversineRouteEstimate,
    buildDriverDistanceResults,
    sortNearbyDriversByDistance,
    roundCoordPair,
} from './RouteCostControl';

const getApiHost = () => {
    const { config } = firebase;
    const settings = store.getState()?.settingsdata?.settings;
    if (typeof window !== 'undefined' && window.location?.origin && settings?.CompanyWebsite === window.location.origin) {
        return window.location.origin;
    }
    return `https://${config.projectId}.web.app`;
};

const placesCache = new Map();
const geocodeCache = new Map();
const placeDetailsCache = new Map();

const cacheGet = (map, key, ttlMs = CACHE_TTL_ROUTES_MS) => {
    const entry = map.get(key);
    if (!entry) return null;
    if (Date.now() - entry.savedAt > ttlMs) {
        map.delete(key);
        return null;
    }
    return entry.data;
};

const cacheSet = (map, key, data) => {
    map.set(key, { data, savedAt: Date.now() });
};

const geocodeCacheKey = (latlng) => {
    const [lat, lng] = String(latlng || '').split(',').map(Number);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return String(latlng || '');
    return roundCoordPair(lat, lng);
};

export const verifyDocument = (imageBase64, docType) => {
    return new Promise((resolve) => {
        const { config } = firebase;
        const settings = store.getState()?.settingsdata?.settings;
        let host = getApiHost();

        // La verificación es "best-effort": nunca debe bloquear al usuario.
        // Si tarda demasiado (red lenta / imagen muy grande en iOS) o falla,
        // resolvemos como "no verificado" para que el usuario pueda continuar.
        let settled = false;
        const finish = (value) => {
            if (settled) return;
            settled = true;
            resolve(value);
        };

        const controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
        const timeoutId = setTimeout(() => {
            if (controller) { try { controller.abort(); } catch (e) {} }
            finish({ checked: false, valid: true, timeout: true });
        }, 20000);

        fetch(`${host}/verify_document`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                "Authorization": "Basic " + base64.encode(config.projectId + ":" + AccessKey)
            },
            body: JSON.stringify({
                image: imageBase64,
                docType: docType
            }),
            signal: controller ? controller.signal : undefined
        }).then(response => {
            return response.json();
        }).then(json => {
            clearTimeout(timeoutId);
            if (json && json.error) {
                // No bloqueamos por un error del verificador.
                finish({ checked: false, valid: true, error: json.error });
            } else {
                finish(json);
            }
        }).catch(error => {
            clearTimeout(timeoutId);
            console.log(error);
            finish({ checked: false, valid: true, error: "verifyDocument Call Error" });
        });
    });
}

export const shouldCallPlacesAutocomplete = (query, lastQuery, lastTimestamp) => {
    const text = String(query || '').trim();
    if (text.length < PLACES_MIN_CHARS) return false;
    if (text === lastQuery) return false;
    if (lastTimestamp && Date.now() - lastTimestamp < PLACES_DEBOUNCE_MS) return false;
    return true;
};

export const getCachedPlaceSuggestions = (query) => {
    const cacheKey = String(query || '').trim().toLowerCase();
    if (!cacheKey) return null;
    return cacheGet(placesCache, cacheKey, CACHE_TTL_PLACES_MS);
};

export const saveCachedPlaceSuggestions = (query, results) => {
    const cacheKey = String(query || '').trim().toLowerCase();
    if (cacheKey) cacheSet(placesCache, cacheKey, results);
};

export const getCachedGeocode = (latlng) => cacheGet(geocodeCache, geocodeCacheKey(latlng), CACHE_TTL_GEOCODING_MS);

export const saveCachedGeocode = (latlng, result) => cacheSet(geocodeCache, geocodeCacheKey(latlng), result);

export const shouldCallReverseGeocoding = (context = {}) => {
    const { isDragging = false, lastCallAt = 0 } = context;
    if (isDragging) return false;
    if (lastCallAt && Date.now() - lastCallAt < 1000) return false;
    return true;
};

export const fetchPlacesAutocomplete = (searchKeyword, sessionToken) => {
    const keyword = String(searchKeyword || '').trim();
    if (keyword.length < PLACES_MIN_CHARS) {
        return Promise.resolve([]);
    }

    const cacheKey = keyword.toLowerCase();
    const cached = getCachedPlaceSuggestions(keyword);
    if (cached) {
        debugLog('PLACES_CACHE_HIT', cacheKey);
        return Promise.resolve(cached);
    }

    return new Promise((resolve,reject)=>{
        const { config } = firebase;
        const settings = store.getState()?.settingsdata?.settings;
        let host = getApiHost()
        fetch(`${host}/googleapi`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                "Authorization": "Basic " + base64.encode(config.projectId + ":" + AccessKey)
            },
            body: JSON.stringify({
                "searchKeyword": searchKeyword,
                "sessiontoken": sessionToken
            })
        }).then(response => {
            return response.json();
        })
        .then(json => {
            if(json && json.searchResults) {
                cacheSet(placesCache, cacheKey, json.searchResults);
                resolve(json.searchResults);
            }else{
                reject(json.error);
            }
        }).catch(error=>{
            console.log(error);
            reject("fetchPlacesAutocomplete Call Error")
        })
    });
}

export const fetchCoordsfromPlace = (place_id) => {
    if (!place_id) {
        return Promise.reject('missing place_id');
    }
    const cached = cacheGet(placeDetailsCache, place_id);
    if (cached) {
        debugLog('PLACE_DETAILS_CACHE_HIT', place_id);
        return Promise.resolve(cached);
    }

    return new Promise((resolve,reject)=>{
        const { config } = firebase;
        const settings = store.getState()?.settingsdata?.settings;
        let host = getApiHost()
        fetch(`${host}/googleapi`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                "Authorization": "Basic " + base64.encode(config.projectId + ":" + AccessKey)
            },
            body: JSON.stringify({
                "place_id": place_id
            })
        }).then(response => {
            return response.json();
        })
        .then(json => {
            if(json && json.coords) {
                cacheSet(placeDetailsCache, place_id, json.coords);
                resolve(json.coords);
            }else{
                reject(json.error);
            }
        }).catch(error=>{
            console.log(error);
            reject("fetchCoordsfromPlace Call Error")
        })
    });
}


export const fetchAddressfromCoords = (latlng) => {
    const cacheKey = geocodeCacheKey(latlng);
    const cached = getCachedGeocode(latlng);
    if (cached) {
        debugLog('GEOCODE_CACHE_HIT', cacheKey);
        return Promise.resolve(cached);
    }

    return new Promise((resolve,reject)=>{
        const { config } = firebase;
        const settings = store.getState()?.settingsdata?.settings;
        let host = getApiHost()
        fetch(`${host}/googleapi`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                "Authorization": "Basic " + base64.encode(config.projectId + ":" + AccessKey)
            },
            body: JSON.stringify({
                "latlng": latlng
            })
        }).then(response => {
            return response.json();
        })
        .then(json => {
            if(json && json.address) {
                cacheSet(geocodeCache, cacheKey, json.address);
                resolve(json.address);
            }else{
                reject(json.error);
            }
        }).catch(error=>{
            console.log(error);
            reject("fetchAddressfromCoords Call Error")
        })
    });
}

/**
 * Matriz de rutas: BLOQUEADA en cliente — usa Haversine local.
 * Evita Compute Route Matrix Pro (costo muy alto con muchos conductores).
 */
export const getDistanceMatrix = (startLoc, destLoc, driverLocations = [], options = {}) => {
    const settings = store.getState()?.settingsdata?.settings;
    console.log('ROUTE_API_SKIPPED_USING_HAVERSINE', { reason: 'matrix', startLoc });

    return new Promise((resolve) => {
        try {
            const [pickupLat, pickupLng] = startLoc.split(',').map(Number);
            let drivers = Array.isArray(driverLocations) ? driverLocations : [];

            if (!drivers.length && typeof destLoc === 'string' && destLoc.includes('|')) {
                drivers = destLoc.split('|').filter(Boolean).map((coord, idx) => {
                    const [lat, lng] = coord.split(',').map(Number);
                    return { id: `dest_${idx}`, location: { lat, lng } };
                });
            }

            const sorted = sortNearbyDriversByDistance(
                { lat: pickupLat, lng: pickupLng },
                drivers,
                {
                    limit: options.limit || 10,
                    radiusKm: (settings && settings.driverRadius) ? settings.driverRadius : 10,
                    convertToMile: !!(settings && settings.convert_to_mile),
                    carTypeName: options.carTypeName || null,
                }
            );

            resolve(buildDriverDistanceResults(sorted, !!(settings && settings.convert_to_mile)));
        } catch (e) {
            resolve([]);
        }
    });
};

/**
 * Ruta individual: solo llama Google si shouldCallGoogleRoutes() lo permite.
 * En caso contrario devuelve estimación Haversine (sin polyline de Google).
 */
export const getDirectionsApi = (startLoc, destLoc, waypoints, options = {}) => {
    const settings = store.getState()?.settingsdata?.settings;
    const reason = options.reason || 'booking_estimate';
    const wp = waypoints || '';

    const cached = getCachedRoute(startLoc, destLoc, wp);
    if (cached) {
        return Promise.resolve(cached);
    }

    if (!shouldCallGoogleRoutes({ reason, settings, serviceId: options.serviceId })) {
        const estimate = buildHaversineRouteEstimate(startLoc, destLoc, wp);
        saveCachedRoute(startLoc, destLoc, wp, estimate);
        return Promise.resolve(estimate);
    }

    return new Promise((resolve, reject) => {
        const { config } = firebase;
        let host = getApiHost();
        const body = {
            start: startLoc,
            dest: destLoc,
            calltype: 'direction',
            routeReason: reason,
            serviceId: options.serviceId || null,
            userId: options.userId || null,
        };
        if (wp) body.waypoints = wp;

        fetch(`${host}/googleapi`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Basic ' + base64.encode(config.projectId + ':' + AccessKey),
            },
            body: JSON.stringify(body),
        })
            .then((response) => response.json())
            .then((json) => {
                if (json.hasOwnProperty('distance_in_km')) {
                    recordRouteApiCall(reason, { serviceId: options.serviceId, userId: options.userId });
                    saveCachedRoute(startLoc, destLoc, wp, json);
                    resolve(json);
                } else {
                    console.log('ROUTE_API_SKIPPED_USING_HAVERSINE', { reason, detail: 'api_error_fallback' });
                    const fallback = buildHaversineRouteEstimate(startLoc, destLoc, wp);
                    saveCachedRoute(startLoc, destLoc, wp, fallback);
                    resolve(fallback);
                }
            })
            .catch((error) => {
                console.log(error);
                const fallback = buildHaversineRouteEstimate(startLoc, destLoc, wp);
                saveCachedRoute(startLoc, destLoc, wp, fallback);
                resolve(fallback);
            });
    });
};

